import { forwardRef } from "react";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * Pill-shaped call-to-action button with a circular arrow docked on the
 * right that nudges forward on hover — the dynamic CTA pattern used
 * throughout the redesign (replaces plain rectangular buttons for primary
 * actions like 'Schedule Consultation' / 'Free Case Evaluation').
 */
export const CTAButton = forwardRef(
  ({ children, variant = "primary", className = "", icon: Icon = ArrowRight, type = "button", ...props }, ref) => {
    const variants = {
      primary:
        "bg-[color:var(--navy-950)] text-white hover:bg-[color:var(--navy-900)] shadow-[0_10px_30px_rgba(11,18,32,0.18)] hover:shadow-[0_14px_40px_rgba(11,18,32,0.24)]",
      brass:
        "bg-[color:var(--gold-500)] text-[color:var(--navy-950)] hover:bg-[color:var(--gold-400)] shadow-[0_10px_30px_rgba(184,154,61,0.28)] hover:shadow-[0_14px_40px_rgba(184,154,61,0.35)]",
      outline:
        "bg-white/5 text-white ring-1 ring-white/25 hover:bg-white/10 shadow-none",
      outlineLight:
        "bg-transparent text-[color:var(--navy-950)] ring-1 ring-slate-300 hover:bg-slate-50 shadow-none",
    };
    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          "inline-flex items-center rounded-full font-semibold pl-6 pr-1.5 py-1.5 gap-3 text-sm transition-[box-shadow,background-color,transform] duration-200 active:scale-[0.98] disabled:opacity-60 disabled:pointer-events-none",
          variants[variant],
          className
        )}
        {...props}
      >
        <span>{children}</span>
        <span
          className={cn(
            "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-transform duration-200 group-hover:translate-x-0.5",
            variant === "brass" ? "bg-[color:var(--navy-950)]/12" : "bg-white/15"
          )}
        >
          <Icon className="h-4 w-4" />
        </span>
      </button>
    );
  }
);
CTAButton.displayName = "CTAButton";
